import type { DocKind } from '../shared/index.js';

type ReportVariant = 'scene' | 'autopsy' | 'lab' | 'police' | 'expert' | 'insurance' | 'medical' | 'internal';
type TicketVariant = 'bus' | 'flight' | 'train' | 'ferry' | 'parking' | 'event' | 'pawn' | 'hotel' | 'other';

const hours = (n: number) => `${n} sa`;

export const s = {
  name: 'Dedektif Masası',
  you: 'Sen',
  pitch: 'Belgeleri birlikte incele, izleri seç, faili ekipçe bul',

  kind: {
    statement: 'İfade',
    report: 'Rapor',
    phoneLog: 'HTS kaydı',
    messages: 'Mesajlar',
    email: 'E-posta',
    receipt: 'Fiş',
    bank: 'Hesap dökümü',
    letter: 'Not',
    newspaper: 'Gazete',
    photo: 'Fotoğraf',
    accessLog: 'Geçiş kaydı',
    ticket: 'Bilet',
    table: 'Liste',
    floorplan: 'Kroki',
    audio: 'Ses kaydı',
  } satisfies Record<DocKind, string>,

  difficulty: { orta: 'Orta', zor: 'Zor', 'çok zor': 'Çok zor' } as Record<string, string>,

  settings: {
    readOnly: 'Vakayı ve kuralları oda sahibi seçer.',
    shelf: 'Vaka rafı',
    shelfHint: 'Bir vaka seç. Dosya herkesin ortak masasına açılır.',
    loading: 'Vakalar yükleniyor…',
    loadError: 'Vaka listesi alınamadı. Sayfayı yenileyip tekrar dene.',
    empty: 'Rafta henüz vaka yok.',
    minutes: (n: number) => `${n} dk`,
    acts: (n: number) => `${n} bölüm`,
    evidence: (n: number) => `${n} belge`,
    solved: (rank: string) => `Çözüldü, ${rank}`,
    selected: 'Seçili',
    sessions: 'Devam eden dosyalar',
    sessionsHint: 'Yarım kalan bir soruşturmayı kaldığı yerden sürdür.',
    /** `act` sunucudan 1'den başlayarak gelir. */
    sessionAct: (act: number, acts: number) => `Bölüm ${act}/${acts}`,
    sessionHours: (n: number) => `${n} sa harcandı`,
    leadMode: 'İzlere kim karar verir',
    leadModes: { vote: 'Oylama', host: 'Oda sahibi' } as Record<'vote' | 'host', string>,
    leadModeHint: {
      vote: 'Biri bir iz önerir, çoğunluk evet derse iz açılır. Oda sahibi beklemeden de açabilir.',
      host: 'Herkes iz önerebilir; açıp açmamaya oda sahibi karar verir.',
    } as Record<'vote' | 'host', string>,
    hints: 'Amirden ipucu',
    hintsHint: 'Her bölümde en fazla 2 ipucu. Her ipucu sonuçtan 5 puan düşer.',
  },

  ago: {
    now: 'az önce',
    min: (n: number) => `${n} dk önce`,
    hour: (n: number) => `${n} sa önce`,
    day: (n: number) => `${n} gün önce`,
  },

  tabs: { board: 'Pano', cabinet: 'Kanıtlar', interviews: 'Sorgular', leads: 'İzler', notes: 'Notlar' },

  top: {
    secret: 'GİZLİ',
    online: (n: number) => `${n} dedektif çevrim içi`,
    code: 'Oda kodu',
    act: (i: number, total: number) => `Bölüm ${i + 1}/${total}`,
    phase: {
      scene: 'Bölüm açılışı',
      investigate: 'Soruşturma',
      accuse: 'Suçlama',
      reconcile: 'Karşılaştırma',
      result: 'Dosya kapandı',
    } as Record<string, string>,
  },

  scene: {
    act: (i: number, total: number) => `Bölüm ${i + 1} / ${total}`,
    continue: 'Okudum, devam',
    waiting: (ready: number, total: number) => `Diğerleri okuyor (${ready}/${total})`,
    hostSkip: 'Beklemeden başlat',
    narration: 'Anlatıcıyı dinle',
    narrationPause: 'Anlatıcıyı duraklat',
    narrationNow: 'Anlatıcı okuyor',
    narrationStop: 'Durdur',
    close: 'Masaya dön',
    replay: 'Bölüm sahneleri',
    imageMissing: 'Sahne fotoğrafı',
  },

  board: {
    title: 'Pano',
    suspects: 'Şüpheliler',
    victim: (genre: string) => (genre === 'cinayet' || genre === 'zehirlenme' ? 'Maktul' : 'Mağdur'),
    others: 'Diğer kişiler',
    age: (n: number) => `${n} yaşında`,
    markSuspect: 'Şüpheli',
    markClear: 'Temiz',
    markedBy: (mark: string, nick: string) => `${nick}, ${mark.toLocaleLowerCase('tr')} olarak işaretledi`,
    mentions: (n: number) => (n === 0 ? 'Henüz hiçbir belgede geçmiyor' : `${n} belgede geçiyor`),
    showMentions: 'Belgeleri göster',
    locations: 'Mekânlar',
    timeline: 'Zaman çizelgesi',
    timelineEmpty: 'Belgelerden saatler çıktıkça burada sıralanır.',
    more: 'Devamını oku',
    less: 'Kısalt',
    details: (name: string) => `${name}: ayrıntılar`,
  },

  notes: {
    add: 'Not ekle',
    placeholder: 'Kısa bir not yaz',
    delete: 'Notu sil',
    count: (n: number) => (n === 1 ? '1 not' : `${n} not`),
  },

  cabinet: {
    title: 'Kanıt dolabı',
    all: 'Tümü',
    pinned: 'İğnelenenler',
    act: (i: number) => `Bölüm ${i + 1}`,
    person: (name: string) => `${name} geçenler`,
    clearPerson: 'Kişi filtresini kaldır',
    empty: 'Bu filtrede belge yok.',
    new: 'yeni',
    source: (title: string) => `İz: ${title}`,
    sourceStart: 'Bölüm başında geldi',
    pick: 'İncelemek için dolaptan bir belge seç.',
    back: 'Dolaba dön',
    loading: 'Belge getiriliyor…',
    loadError: 'Belge açılamadı. Tekrar dene.',
    retry: 'Tekrar dene',
    zoomIn: 'Büyüt',
    zoomOut: 'Küçült',
    zoomReset: 'Gerçek boyut',
    pin: 'İğnele',
    unpin: 'İğneyi kaldır',
    people: 'Belgede geçenler',
    prev: 'Önceki belge',
    next: 'Sonraki belge',
  },

  leads: {
    title: 'İzler',
    hours: 'Soruşturma saati',
    hoursOf: (spent: number, budget: number) => `${spent} / ${budget} sa`,
    target: (n: number) => `Hedef ${n} sa; altında kalan her saat bonus puan getirir.`,
    actSpent: (spent: number, limit: number | null) => (limit ? `Bu bölümde ${spent} / ${limit} sa` : `Bu bölümde ${spent} sa`),
    budgetOut: 'Saat bütçesi doldu. İz açmaya devam edebilirsiniz; aşılan her 2 saat sonuçtan 1 puan düşer (en fazla 10).',
    overBy: (n: number) => `${n} sa aşıldı`,
    from: (title: string) => `Kaynak: ${title}`,
    cost: hours,
    tooExpensive: 'Bütçeyi aşar',
    propose: 'Bu izi öner',
    open: 'Bu izi aç',
    openNow: 'Oylamadan aç',
    none: 'Açılacak iz kalmadı.',
    available: 'Açılabilir izler',
    opened: 'Açılan izler',
    openedMeta: (act: number, nick: string | null) => (nick ? `Bölüm ${act + 1}, ${nick}` : `Bölüm ${act + 1}`),
    brought: 'Getirdiği belgeler',
    hostOnly: 'İzleri oda sahibi açar. Önerin oda sahibine gider.',
  },

  proposal: {
    lead: (nick: string) => `${nick} bu izi öneriyor`,
    close: (nick: string) => `${nick} dosyayı kapatmayı öneriyor`,
    leadMine: 'Bu izi sen öneriyorsun',
    closeMine: 'Dosyayı kapatmayı sen öneriyorsun',
    tally: (yes: number, needed: number) => `${yes} / ${needed} evet`,
    yes: 'Evet',
    no: 'Hayır',
    cancel: 'Öneriyi geri çek',
    reject: 'Öneriyi geri çevir',
    hostOpen: 'İzi aç',
    hostClose: 'Davayı çöz',
    busy: 'Önce bekleyen öneri sonuçlansın.',
    waitHost: 'Oda sahibinin kararı bekleniyor.',
  },

  hint: {
    title: 'Amirden ipucu',
    ask: 'İpucu iste',
    askCost: (n: number) => `İpucu iste (−${n} puan)`,
    left: (n: number) => (n === 0 ? 'Bu bölümün ipuçları bitti' : `Bu bölümde ${n} ipucu kaldı`),
    confirm: 'İpucu sonuçtan 5 puan düşer. Yine de isteyelim mi?',
    confirmYes: 'Evet, ipucu iste',
    cancel: 'Vazgeç',
    used: (act: number) => `Bölüm ${act + 1}`,
  },

  close: {
    title: 'Dosyayı kapat',
    hint: 'Kapatınca herkes suçlama formunu doldurur. Oda sahibi isterse soruşturmaya dönebilir.',
    propose: 'Davayı çözmeyi öner',
    direct: 'Davayı çöz',
    tag: 'Kim · Nasıl · Neden',
    confirm: 'Dosya kapanıp suçlama aşamasına geçilecek. Emin misin?',
    confirmYes: 'Evet, kapat',
    hostOnly: 'Dosyayı oda sahibi kapatır.',
    notYet: 'Dosya henüz kapatılamaz; ilk bölümün izlerini incelemeye devam edin.',
  },

  notebook: {
    title: 'Ortak not defteri',
    placeholder: 'Herkesin gördüğü ortak defter. Şüpheleri, çelişkileri, saatleri buraya yazın.',
    saving: 'Kaydediliyor…',
    saved: 'Kaydedildi',
    lastBy: (nick: string, when: string) => `Son düzenleyen ${nick}, ${when}`,
    conflict: 'Defteri aynı anda başkası da düzenledi; son hâli yüklendi. Kaydedilmeyen metnin aşağıda.',
    lost: 'Kaydedilmeyen metnin',
    dismiss: 'Kapat',
    chars: (n: number, max: number) => `${n} / ${max}`,
  },

  log: { title: 'Olay akışı', empty: 'Henüz bir şey olmadı.' },

  accuse: {
    title: 'Suçlama formu',
    lead: 'Dosya kapandı. Herkes formu kendisi doldurur; sonra oda sahibi formları toplar ve ekip cevabı birlikte belirlenir.',
    points: (n: number) => `${n} puan`,
    pickSuspect: 'Şüphelilerden birini seç',
    pickEvidence: 'Açılmış belgelerden birini seç',
    submit: 'Formu gönder',
    update: 'Formu güncelle',
    sent: 'Formun gönderildi. İstersen değiştirip yeniden gönderebilirsin.',
    incomplete: (n: number) => (n === 1 ? '1 soru boş' : `${n} soru boş`),
    submitted: 'Formunu gönderenler',
    collect: 'Formları topla',
    collectHint: (n: number, total: number) => `${n}/${total} form geldi. Toplayınca cevaplar karşılaştırılır.`,
    reopen: 'Soruşturmaya dön',
    waitHost: 'Herkes gönderince oda sahibi formları toplayacak.',
    browse: 'Dosyayı incele',
    backToForm: 'Forma dön',
    browsing: 'Suçlama aşamasındasınız. Belgelere göz atabilirsiniz; yeni iz açılamaz.',
  },

  reconcile: {
    title: 'Ekip cevabı',
    lead: 'Formlar yan yana. Çoğunluğun cevabı seçili geldi; oda sahibi tartışmaya göre değiştirebilir.',
    noVotes: 'Kimse cevaplamadı',
    hostPick: 'Ekip cevabı yap',
    teamAnswer: 'Ekip cevabı',
    submit: 'Ekip cevabını teslim et',
    missing: 'Her soru için bir ekip cevabı seçin.',
    waitHost: 'Oda sahibi ekip cevabını teslim edecek.',
  },

  result: {
    stamp: 'Dosya kapandı',
    solved: 'Vaka çözüldü',
    unsolved: 'Vaka çözülemedi',
    endHost: 'Okumanız bitince alttaki "Oyunu bitir" ile puanları kaydedip lobiye dönün.',
    endGuest: 'Oda sahibi oyunu bitirince puanlar kaydedilip lobiye dönülecek.',
    score: 'Puan',
    rank: 'Derece',
    breakdown: 'Puan dökümü',
    questions: 'Sorular',
    team: 'Ekip cevabı',
    correct: 'Doğru cevap',
    noAnswer: 'Cevap yok',
    ok: 'Doğru',
    wrong: 'Yanlış',
    individual: 'Kişisel formlar',
    individualOf: (c: number, t: number) => `${c}/${t} doğru`,
    culprits: 'Fail',
    story: 'Gerçekte ne oldu',
    timeline: 'Gerçek zaman çizelgesi',
    chain: 'Kanıt zinciri',
    found: 'bulundu',
    missed: 'kaçırıldı',
    herrings: 'Yanıltıcı ipuçları',
    characters: 'Herkesin sırrı',
    whereabouts: 'O saatte',
    lies: 'İfadesindeki yalanlar',
    pts: (n: number) => (n > 0 ? `+${n}` : `${n}`),
  },

  doc: {
    report: {
      scene: 'Olay yeri inceleme',
      autopsy: 'Adli tıp',
      lab: 'Kriminal laboratuvar',
      police: 'Emniyet',
      expert: 'Bilirkişi',
      insurance: 'Sigorta',
      medical: 'Tıbbi kayıt',
      internal: 'İç yazışma',
    } satisfies Record<ReportVariant, string>,
    reportStamp: {
      scene: 'ASLI GİBİDİR',
      autopsy: 'ADLİ TIP',
      lab: 'İNCELENDİ',
      police: 'ASLI GİBİDİR',
      expert: 'BİLİRKİŞİ',
      insurance: 'HASAR DOSYASI',
      medical: 'GİZLİDİR',
      internal: 'GİZLİ',
    } satisfies Record<ReportVariant, string>,
    ticket: {
      bus: 'Otobüs bileti',
      flight: 'Biniş kartı',
      train: 'Tren bileti',
      ferry: 'Vapur bileti',
      parking: 'Otopark fişi',
      event: 'Etkinlik bileti',
      pawn: 'Rehin makbuzu',
      hotel: 'Otel kaydı',
      other: 'Bilet',
    } satisfies Record<TicketVariant, string>,
    statementTitle: 'İfade tutanağı',
    capacity: { tanık: 'Tanık', şüpheli: 'Şüpheli', müşteki: 'Müşteki', bilirkişi: 'Bilirkişi' } as Record<string, string>,
    fields: {
      name: 'Adı soyadı',
      age: 'Yaşı',
      capacity: 'Sıfatı',
      role: 'İşi / konumu',
      takenBy: 'İfadeyi alan',
      place: 'Yer',
      datetime: 'Tarih, saat',
      no: 'Sayı',
      date: 'Tarih',
      subject: 'Konu',
      subscriber: 'Abone',
      number: 'Numara',
      period: 'Dönem',
      holder: 'Hesap sahibi',
      iban: 'IBAN',
      site: 'Tesis',
      from: 'Kimden',
      to: 'Kime',
      cc: 'Bilgi',
    },
    asked: 'Soruldu',
    answered: 'Cevaben',
    statementClose: 'İfadem okundu, söylediklerimin aynen yazıldığını beyan ederim.',
    signGiver: 'İfade veren',
    signTaker: 'İfadeyi alan',
    signature: 'İmza',
    htsTitle: 'Abone iletişim kayıtları (HTS)',
    hts: { datetime: 'Tarih / saat', type: 'Tür', other: 'Karşı taraf', duration: 'Süre', tower: 'Baz istasyonu' },
    deleted: 'Bu mesaj silindi',
    recovered: 'Kurtarılan içerik',
    attachments: (n: number) => (n === 1 ? '1 ek' : `${n} ek`),
    total: 'Toplam',
    payment: 'Ödeme',
    bankTitle: 'Hesap hareketleri',
    bank: { date: 'Tarih', desc: 'Açıklama', amount: 'Tutar', balance: 'Bakiye' },
    bankIn: 'Toplam giriş',
    bankOut: 'Toplam çıkış',
    condition: (c: string) => `Belgenin durumu: ${c}`,
    access: { time: 'Saat', card: 'Kart', holder: 'Kart sahibi', door: 'Kapı', result: 'Sonuç' },
    accessTitle: 'Geçiş olayları raporu',
    accessSummary: (rows: number, rej: number, alarm: number) => `${rows} kayıt, ${rej} ret, ${alarm} alarm`,
    receiptNo: 'FİŞ NO',
    photoMissing: 'Fotoğraf henüz basılmadı. Dosyadaki tarif:',
    photoTag: (t: string) => `Delil ${t}`,
    rec: 'KAYIT',
    floorplanTitle: 'Kroki',
    legend: 'Lejant',
    scale: '1 m',
    north: 'K',
    planMissing: 'Bu mekânın krokisi dosyada yok.',
    marker: { body: 'Ceset / kişi', camera: 'Kamera', item: 'Eşya', stain: 'İz / leke', note: 'Not' } as Record<string, string>,
    door: 'Kapı',
    window: 'Pencere',
    page: (n: number, total: number) => `Sayfa ${n} / ${total}`,
  },

  audio: {
    variant: {
      'phone-call': 'Telefon görüşmesi',
      'emergency-call': 'İhbar hattı kaydı',
      voicemail: 'Sesli mesaj',
      'voice-note': 'Sesli not',
      recording: 'Ses kaydı',
      interview: 'Görüşme kaydı',
    } as Record<string, string>,
    play: 'Kaydı oynat',
    pause: 'Duraklat',
    seek: 'Kayıtta konum',
    missing: 'Kayıt henüz seslendirilmedi. Dökümü okuyabilirsiniz.',
    loadError: 'Kayıt çalınamadı. Dökümü okuyabilirsiniz.',
    showTranscript: 'Dökümü göster',
    hideTranscript: 'Dökümü gizle',
    transcript: 'Döküm',
    silence: (n: number) => `[${n} sn sessizlik]`,
    ambience: (a: string) => `[${a}]`,
    recNo: 'Kayıt no',
    unknown: 'Bilinmeyen ses',
  },

  interview: {
    title: 'Sorgular',
    open: 'Sorgula',
    pick: 'Sorgu seç',
    unread: (n: number) => (n === 1 ? '1 yeni cevap' : `${n} yeni cevap`),
    fresh: 'Yeni sorgu',
    act: (i: number) => `Bölüm ${i + 1}`,
    empty: 'Henüz açık sorgu yok. İzler ve yeni bölümler sorgu odasına yeni kişiler getirir.',
    transcript: 'Tutanak',
    transcriptEmpty: 'Henüz soru sorulmadı. Aşağıdan bir soru seç ya da bir belge göster.',
    asked: (nick: string) => `${nick} sordu`,
    showed: (nick: string) => `${nick} belge gösterdi`,
    shownDoc: 'Gösterilen belge',
    deflect: 'Kaçamak cevap',
    broke: 'İfade değişti',
    unlocked: 'Dosyaya eklendi',
    listen: 'Kaydı dinle',
    pause: 'Duraklat',
    audioError: 'Kayıt çalınamadı',
    questions: 'Sorular',
    newQ: 'yeni',
    noQuestions: 'Şu an sorulacak yeni soru yok. Yeni belgeler gelince yeni sorular açılabilir.',
    free: 'Soru sormak saat harcatmaz. Tutanak herkesin.',
    show: 'Belge göster',
    showTo: (name: string) => `${name} karşısına belge koy`,
    search: 'Belge ara',
    noMatch: 'Bu aramayla eşleşen belge yok.',
    shownTag: 'Gösterildi',
    confirm: 'Göster',
    cancel: 'Vazgeç',
    hit: 'Savunması çöktü. Tutanağa bak.',
    miss: 'Belge bir şey değiştirmedi.',
    closed: 'Soruşturma kapandı; tutanaklar yalnızca okunabilir.',
    back: 'Sorgulara dön',
  },

  errors: {
    generic: 'Hamle gönderilemedi. Bağlantını kontrol edip tekrar dene.',
  },

  howto: {
    title: 'Nasıl oynanır',
  },
};
